import toast from "react-hot-toast";
import { useState } from "react";
import { useHistory } from "react-router-dom";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import { userUpdateStatus } from "../../../api/api.service";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { ConfirmationModal } from "../../../components/CustomModal";
import ModalSuccessImage from "../../../assets/images/app/common/aou-modal-success-img.svg";
import typography from "../typography";
import {
  GRADE_BAND_LABELS,
  ROLE_LABELS,
  SIGN_IN_LABELS,
  describeAccess,
  formatDate,
  formatRelative,
} from "./insightLabels";

const toggleConfig = {
  trueContent: typography.ACTIVE,
  falseContent: typography.INACTIVE,
};

const SCHOOL_ADMIN_STATUS = {
  PENDING: "Waiting for approval",
  APPROVED: "Approved",
  DECLINED: "Declined",
  REVOKED: "Revoked",
};

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

/**
 * Who this is and whether they have access — the facts support asks first.
 *
 * Replaces the mobile-era fields (occupation, gender, phone, date of birth,
 * age, and a "Free" plan badge read from a column that no longer decides
 * access). Access now comes from the same entitlement the teacher portal
 * enforces.
 */
export default function Biography({ overview, onStatusChanged }) {
  const history = useHistory();
  const [isModalActive, setIsModalActive] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  const profile = overview?.profile;
  const access = describeAccess(overview?.access);
  const activeRoom =
    overview?.classrooms?.find((room) => room.isActive) ??
    overview?.classrooms?.[0];

  const updateUserStatus = async () => {
    setIsModalActive(false);
    const next =
      profile.status === typography.ACTIVE_CAPS
        ? typography.INACTIVE_CAPS
        : typography.ACTIVE_CAPS;
    try {
      setIsStatusUpdating(true);
      await userUpdateStatus({ id: profile.id, bodyData: { status: next } });
      toast.success("Status has been updated successfully!");
      onStatusChanged?.(next);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  if (!profile) return null;

  const facts = [
    { label: "Role", value: ROLE_LABELS[profile.role] || "Not given" },
    {
      label: "School",
      value: profile.school || "Not given",
      sub: profile.district,
    },
    {
      label: "Classroom",
      value: activeRoom?.name || "None yet",
      sub: activeRoom?.gradeBand
        ? GRADE_BAND_LABELS[activeRoom.gradeBand]
        : null,
    },
    {
      label: "Signs in with",
      value: profile.signInMethods?.length
        ? profile.signInMethods
            .map((method) => SIGN_IN_LABELS[method] || method)
            .join(", ")
        : "—",
    },
    { label: "Joined", value: formatDate(profile.joinedAt) },
    { label: "Last active", value: formatRelative(profile.lastActiveAt) },
    {
      label: "Onboarding",
      value: profile.onboarded ? "Completed" : "Not finished",
    },
    { label: "Timezone", value: profile.timezone || "—" },
    // Only for someone who asked to be a school administrator: whether that
    // request was approved is the first thing support needs to know about them.
    overview?.schoolAdmin
      ? {
          label: "School admin access",
          value:
            SCHOOL_ADMIN_STATUS[overview.schoolAdmin.status] ||
            overview.schoolAdmin.status,
          sub: overview.schoolAdmin.reviewedAt
            ? `Reviewed ${formatDate(overview.schoolAdmin.reviewedAt)}`
            : `Requested ${formatDate(overview.schoolAdmin.requestedAt)}`,
        }
      : null,
  ].filter(Boolean);

  return (
    <>
      <HeadingWithBackArrow
        backArrowClickHandler={() =>
          history.push(APP_ROUTES.CUSTOMER_MANAGEMENT)
        }
        headingTitle={typography.CUSTOMER_DETAILS}
        toggleButtonConfig={toggleConfig}
        toggleValue={profile.status !== typography.INACTIVE_CAPS}
        isDisabled={isStatusUpdating}
        toggleHandler={() => setIsModalActive(true)}
      />

      <section className="cd-hero" aria-label="Customer summary">
        <div className="cd-hero__identity">
          <div className="cd-avatar" aria-hidden="true">
            {initials(profile.name)}
          </div>
          <div className="cd-hero__names">
            <h2 className="cd-hero__name">{profile.name}</h2>
            <p className="cd-hero__email">{profile.email}</p>
          </div>
          <div className={`cd-access cd-access--${access.tone}`}>
            <span className="cd-access__label">{access.label}</span>
            {access.detail ? (
              <span className="cd-access__detail">{access.detail}</span>
            ) : null}
          </div>
        </div>

        <dl className="cd-facts">
          {facts.map((fact) => (
            <div className="cd-fact" key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>
                {fact.value}
                {fact.sub ? (
                  <span className="cd-fact__sub">{fact.sub}</span>
                ) : null}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <ConfirmationModal
        isOpen={isModalActive}
        hasCustomConfig
        size="md"
        onNo={() => setIsModalActive(false)}
        onYes={updateUserStatus}
        customTitle={typography.STATUS_UPDATE_CONFIRMATION_MESSAGE}
        customYesBtnText={typography.YES}
        customNoBtnText={typography.NO}
        customImageSrc={ModalSuccessImage}
      />
    </>
  );
}
